/* ============================================================================
   KITSTERS · GIFTING HUB · kitsters-gifting.js
   Productionised 1:1 from the approved Claude Design "Gifting Hub" frame.
   Drives: rotating hero headlines, hero 3-D parallax + confetti, count-up
   stats, festive countdown, 3-step Gift Finder wizard, carousels, tilt cards,
   the 3-D gift card, scroll reveal and the celebration canvas.
   No framework. Everything gated by prefers-reduced-motion. Degrades: content
   is server-rendered; without JS the quiz hides and the kit pool shows.
   ========================================================================== */
(function () {
  "use strict";

  var reduced = window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  document.documentElement.classList.remove("no-js");

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  function readJSON(sel, scope) {
    var el = (scope || document).querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent || "null"); } catch (e) { return null; }
  }

  /* ------------------------------------------------------------- CONFETTI
     Canvas burst ported from the design frame (same physics + palette). */
  function fireConfetti(canvas, ox, oy, count) {
    if (!canvas || reduced) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    var ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    var styles = getComputedStyle(document.documentElement);
    var colors = [
      (styles.getPropertyValue("--color-yellow") || "#fdda0d").trim(),
      (styles.getPropertyValue("--color-pink") || "#ff4683").trim(),
      (styles.getPropertyValue("--color-indigo") || "#465ae6").trim(),
      (styles.getPropertyValue("--color-black") || "#0a0a0a").trim(),
      "#ffffff"
    ];
    var W = rect.width, H = rect.height;
    var parts = [];
    var n = count || 70;
    for (var i = 0; i < n; i++) {
      var a = Math.random() * Math.PI * 2;
      var sp = 3 + Math.random() * 8;
      parts.push({
        x: ox, y: oy,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 5,
        g: 0.16 + Math.random() * 0.12,
        s: 5 + Math.random() * 7,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.32,
        col: colors[i % colors.length],
        life: 1,
        sq: Math.random() > 0.5
      });
    }
    if (canvas.__cf) cancelAnimationFrame(canvas.__cf);
    var black = colors[3];
    function draw() {
      ctx.clearRect(0, 0, W, H);
      var alive = 0;
      for (var j = 0; j < parts.length; j++) {
        var p = parts[j];
        if (p.life <= 0) continue;
        p.vy += p.g; p.vx *= 0.99; p.x += p.vx; p.y += p.vy;
        p.rot += p.vr; p.life -= 0.007;
        if (p.y > H + 24) p.life = 0;
        if (p.life <= 0) continue;
        alive++;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.max(0, Math.min(1, p.life * 1.4));
        ctx.fillStyle = p.col;
        ctx.strokeStyle = black;
        ctx.lineWidth = 1.2;
        if (p.sq) {
          ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s);
          ctx.strokeRect(-p.s / 2, -p.s / 2, p.s, p.s);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.s / 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
        ctx.restore();
      }
      if (alive > 0) canvas.__cf = requestAnimationFrame(draw);
      else { ctx.clearRect(0, 0, W, H); canvas.__cf = null; }
    }
    canvas.__cf = requestAnimationFrame(draw);
  }

  function celebrate() {
    if (reduced) return;
    requestAnimationFrame(function () {
      var c = document.getElementById("kg-fx");
      if (!c) return;
      var r = c.getBoundingClientRect();
      if (r.width) fireConfetti(c, r.width / 2, r.height * 0.32, 150);
    });
  }

  /* --------------------------------------------------------------- REVEAL */
  function initReveal() {
    var items = document.querySelectorAll(".kg-rev");
    if (!items.length) return;
    if (reduced || !("IntersectionObserver" in window)) return;
    document.documentElement.classList.add("kg-anim");
    var vh = window.innerHeight || 800;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          en.target.classList.add("is-in");
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.12 });
    items.forEach(function (it) {
      if (it.getBoundingClientRect().top < vh * 0.92) it.classList.add("is-in");
      else io.observe(it);
    });
  }

  /* ----------------------------------------------------------------- HERO */
  function initHero() {
    var hero = document.querySelector("[data-kg-hero]");
    if (!hero) return;

    // Rotating headlines - 3 variants server-rendered; one visible at a time.
    var headlines = hero.querySelectorAll("[data-kg-headline]");
    var dots = hero.querySelectorAll("[data-kg-dot]");
    var current = 0;
    var timer = null;
    function show(i) {
      current = i;
      headlines.forEach(function (h, idx) { h.hidden = idx !== i; });
      dots.forEach(function (d, idx) {
        d.setAttribute("aria-current", idx === i ? "true" : "false");
      });
    }
    if (headlines.length > 1) {
      if (!reduced) {
        timer = setInterval(function () {
          show((current + 1) % headlines.length);
        }, 4500);
      }
      dots.forEach(function (d, idx) {
        d.addEventListener("click", function () {
          if (timer) { clearInterval(timer); timer = null; }
          show(idx);
        });
      });
    }

    // Pointer parallax on the gift-box scene.
    var stage = document.getElementById("kg-herostage");
    var scene = document.getElementById("kg-hero-scene");
    if (stage && scene && !reduced) {
      var sats = scene.querySelectorAll(".kg-satellite");
      stage.addEventListener("pointermove", function (ev) {
        var r = stage.getBoundingClientRect();
        var px = (ev.clientX - r.left) / r.width - 0.5;
        var py = (ev.clientY - r.top) / r.height - 0.5;
        scene.style.setProperty("--ry", (px * 12) + "deg");
        scene.style.setProperty("--rx", (-py * 9) + "deg");
        sats.forEach(function (s) {
          var d = parseFloat(s.getAttribute("data-depth")) || 1;
          s.style.transform = "translate(" + (px * d * 16) + "px," + (py * d * 16) + "px)";
        });
      }, { passive: true });
      stage.addEventListener("pointerleave", function () {
        scene.style.setProperty("--ry", "0deg");
        scene.style.setProperty("--rx", "0deg");
        sats.forEach(function (s) { s.style.transform = ""; });
      });
    }

    // Ambient confetti pops around the box.
    if (!reduced) {
      var fire = function () {
        var c = document.getElementById("kg-hero-confetti");
        if (!c) return;
        var r = c.getBoundingClientRect();
        if (r.width) fireConfetti(c, r.width / 2, r.height * 0.4, 60);
      };
      setTimeout(fire, 1500);
      setInterval(fire, 4200);
    }
  }

  /* ---------------------------------------------------------------- STATS */
  function initStats() {
    var wrap = document.getElementById("kg-stats");
    if (!wrap) return;
    var nums = wrap.querySelectorAll("[data-countup]");
    if (!nums.length) return;
    if (reduced || !("IntersectionObserver" in window)) return; // final values already in DOM
    var done = false;
    function animate() {
      if (done) return;
      done = true;
      nums.forEach(function (el) {
        var target = parseInt(el.getAttribute("data-countup") || "0", 10);
        if (!target) return;
        var suffix = el.getAttribute("data-suffix") || "";
        var t0 = performance.now();
        var dur = 1600;
        function step(now) {
          var p = Math.min(1, (now - t0) / dur);
          var e = 1 - Math.pow(1 - p, 3);
          /* en-US, not en-IN: Indian digit grouping is lakh-based, so 100000 renders
             as "1,00,000" instead of "100,000". Identical below six figures, which is
             why it has never shown on the current counters — and wrong the day one of
             them passes 100,000. */
          el.textContent = Math.round(target * e).toLocaleString("en-US") + suffix;
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { animate(); io.disconnect(); }
      });
    }, { threshold: 0.35 });
    io.observe(wrap);
  }

  /* ------------------------------------------------------------ COUNTDOWN */
  function initCountdown() {
    var sec = document.querySelector("[data-kg-countdown]");
    if (!sec) return;
    var fests = readJSON("[data-kg-festivals]", sec) || [];
    var main = sec.querySelector("[data-kg-cd-main]");
    var evergreenMain = sec.querySelector("[data-kg-cd-evergreen]");
    var nextCard = sec.querySelector("[data-kg-cd-next]");

    var now = new Date();
    var upcoming = fests
      .map(function (f) {
        f._t = new Date(f.date + "T09:00:00").getTime();
        return f;
      })
      .filter(function (f) { return !isNaN(f._t) && f._t > now.getTime(); })
      .sort(function (a, b) { return a._t - b._t; });

    if (!upcoming.length) {
      if (main) main.hidden = true;
      if (nextCard) nextCard.hidden = true;
      if (evergreenMain) evergreenMain.hidden = false;
      return;
    }
    var primary = upcoming[0];
    var secondary = upcoming[1] || null;
    if (evergreenMain) evergreenMain.hidden = true;
    if (main) main.hidden = false;

    function setText(sel, val) {
      var el = sec.querySelector(sel);
      if (el) el.textContent = val;
    }
    setText("[data-kg-cd-date]", "Next up · " + primary.dateLabel);
    setText("[data-kg-cd-name]", primary.name);
    setText("[data-kg-cd-line]", primary.line);
    var icon = sec.querySelector("[data-kg-cd-icon]");
    if (icon) icon.className = "ph-fill " + (primary.icon || "ph-gift");
    var cta = sec.querySelector("[data-kg-cd-cta-label]");
    if (cta) cta.textContent = primary.cta || "Shop gifts";
    var ctaLink = sec.querySelector("[data-kg-cd-cta]");
    if (ctaLink && primary.link) ctaLink.setAttribute("href", primary.link);

    if (secondary && nextCard) {
      nextCard.hidden = false;
      setText("[data-kg-cd-next-name]", secondary.name);
      setText("[data-kg-cd-next-date]", secondary.dateLabel);
      var sIcon = sec.querySelector("[data-kg-cd-next-icon]");
      if (sIcon) sIcon.className = "ph-fill " + (secondary.icon || "ph-calendar");
    } else if (nextCard) {
      nextCard.hidden = true;
    }

    var dEl = sec.querySelector("[data-kg-d]"), hEl = sec.querySelector("[data-kg-h]"),
        mEl = sec.querySelector("[data-kg-m]"), sEl = sec.querySelector("[data-kg-s]");
    function pad(x) { return String(x).padStart(2, "0"); }
    function tick() {
      var diff = primary._t - Date.now();
      if (diff <= 0) { clearInterval(timer); initCountdown(); return; }
      var d = Math.floor(diff / 86400000); diff -= d * 86400000;
      var h = Math.floor(diff / 3600000); diff -= h * 3600000;
      var m = Math.floor(diff / 60000); diff -= m * 60000;
      var s = Math.floor(diff / 1000);
      if (dEl) dEl.textContent = d;
      if (hEl) hEl.textContent = pad(h);
      if (mEl) mEl.textContent = pad(m);
      if (sEl) sEl.textContent = pad(s);
    }
    tick();
    var timer = setInterval(tick, 1000);
  }

  /* -------------------------------------------------------- GIFT FINDER
     3-step wizard. Matching runs against the server-rendered product pool:
     love (interest) is a HARD filter by collection handle, who (recipient)
     is a SOFT boost, budget is a HARD price band - collections, not tags
     (store-wide tags are empty; see CLAUDE.md "Gift Finder matches by
     COLLECTION"). */
  function initFinder() {
    var finder = document.querySelector("[data-kg-finder]");
    if (!finder) return;
    var cfg = readJSON("[data-kg-finder-config]", finder) || {};
    var whoMap = cfg.who || {};
    var loveMap = cfg.love || {};
    var budgetMap = cfg.budget || {};

    var quiz = finder.querySelector("[data-kg-quiz]");
    var results = finder.querySelector("[data-kg-results]");
    var resultsRow = finder.querySelector("[data-kg-results-row]");
    var resultsTitle = finder.querySelector("[data-kg-results-title]");
    var matchCount = finder.querySelector("[data-kg-match-count]");
    var empty = finder.querySelector("[data-kg-empty]");
    var pool = finder.querySelector("[data-kg-pool]");
    if (!quiz || !results || !pool) return;

    var state = { step: 1, who: null, love: null, budget: null };
    var labels = { who: "", love: "" };

    var steps = quiz.querySelectorAll("[data-kg-step]");
    var progDots = quiz.querySelectorAll(".kg-finder__prog-dot");
    var progBars = quiz.querySelectorAll(".kg-finder__prog-bar");
    var backBtn = quiz.querySelector("[data-kg-back]");
    var nextBtn = quiz.querySelector("[data-kg-next]");
    var nextLabel = quiz.querySelector("[data-kg-next-label]");
    var nextIcon = quiz.querySelector("[data-kg-next-icon]");

    function canNext() {
      if (state.step === 1) return !!state.who;
      if (state.step === 2) return !!state.love;
      return !!state.budget;
    }
    function render() {
      steps.forEach(function (s) {
        s.hidden = parseInt(s.getAttribute("data-kg-step"), 10) !== state.step;
      });
      progDots.forEach(function (d, i) { d.classList.toggle("is-active", state.step >= i + 1); });
      progBars.forEach(function (b, i) { b.classList.toggle("is-active", state.step >= i + 1); });
      backBtn.classList.toggle("is-visible", state.step > 1);
      var last = state.step === 3;
      if (nextLabel) nextLabel.textContent = last ? "Find their gift" : "Next";
      if (nextIcon) nextIcon.className = "ph-bold " + (last ? "ph-magic-wand" : "ph-arrow-right");
      var ok = canNext();
      nextBtn.classList.toggle("is-ready", ok);
      nextBtn.setAttribute("aria-disabled", ok ? "false" : "true");
    }

    quiz.querySelectorAll("[data-kg-opt]").forEach(function (opt) {
      opt.addEventListener("click", function () {
        var group = opt.closest("[data-kg-group]").getAttribute("data-kg-group");
        var val = opt.getAttribute("data-kg-opt");
        state[group] = val;
        if (group === "who" || group === "love") {
          labels[group] = opt.getAttribute("data-kg-label") || "";
        }
        opt.closest("[data-kg-group]").querySelectorAll("[data-kg-opt]").forEach(function (o) {
          o.setAttribute("aria-pressed", o === opt ? "true" : "false");
        });
        render();
      });
    });

    backBtn.addEventListener("click", function () {
      state.step = Math.max(1, state.step - 1);
      render();
    });

    nextBtn.addEventListener("click", function () {
      if (!canNext()) return;
      if (state.step < 3) { state.step += 1; render(); return; }
      submit();
    });

    /* Budget RANKS, it does not exclude (owner, 2026-09-08). It used to be a hard
       filter alongside `love`, and with this catalogue that guarantees dead ends:
       the clay and keepsake collections top out at $49 and sun prints holds two
       kits, so no arrangement of three bands can keep every answer path populated
       - the best possible split still empties 4 of 15 interest/budget pairs, and
       the shipped $50/$75 split emptied 7. A gift finder whose job is to recommend
       should not answer "nothing".

       Root cause of the worst of it is store data, not this file: four of the five
       combos ($84.99-$97) sit in shop-all-diy-kits and NO themed collection, so the
       top price tier was unreachable from any interest. Logged for the owner; if
       they are merchandised properly this ranking still puts them first.

       `love` stays a hard filter - someone who says "clay" should not be shown
       tote bags. */
    function matchPool() {
      var loveHandles = loveMap[state.love] || [];
      var whoHandles = whoMap[state.who] || [];
      var band = budgetMap[state.budget] || null;
      var tiers = [[], [], [], []];
      pool.querySelectorAll("[data-kg-card]").forEach(function (card) {
        var price = parseFloat(card.getAttribute("data-price") || "0");
        var cols = (card.getAttribute("data-collections") || "").split(",");
        var inLove = !loveHandles.length || loveHandles.some(function (h) { return cols.indexOf(h) !== -1; });
        if (!inLove) return;
        var inWho = whoHandles.some(function (h) { return cols.indexOf(h) !== -1; });
        var inBudget = !band || (price >= band[0] && price <= band[1]);
        /* 0 best: right person and right price. 3 worst: neither. */
        tiers[(inWho ? 0 : 2) + (inBudget ? 0 : 1)].push(card);
      });
      return tiers[0].concat(tiers[1], tiers[2], tiers[3]);
    }

    function submit() {
      var matches = matchPool();
      quiz.hidden = true;
      if (!matches.length) {
        empty.hidden = false;
        results.hidden = true;
      } else {
        empty.hidden = true;
        results.hidden = false;
        resultsRow.innerHTML = "";
        matches.slice(0, 4).forEach(function (card) {
          resultsRow.appendChild(card.cloneNode(true));
        });
        if (matchCount) matchCount.textContent = Math.min(matches.length, 4) + " matches";
        if (resultsTitle) {
          resultsTitle.textContent = "Perfect for a " + (labels.who || "gift") +
            " who loves " + (labels.love ? labels.love.toLowerCase() : "this");
        }
        celebrate();
      }
      finder.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
    }

    finder.querySelectorAll("[data-kg-restart]").forEach(function (b) {
      b.addEventListener("click", function () {
        state = { step: 1, who: null, love: null, budget: null };
        labels = { who: "", love: "" };
        quiz.querySelectorAll("[data-kg-opt]").forEach(function (o) {
          o.setAttribute("aria-pressed", "false");
        });
        results.hidden = true;
        empty.hidden = true;
        quiz.hidden = false;
        render();
      });
    });

    render();
  }

  /* ------------------------------------------------------ CAROUSEL ARROWS */
  function initArrows() {
    document.querySelectorAll("[data-kg-scroll]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var target = document.getElementById(btn.getAttribute("data-kg-scroll"));
        if (!target) return;
        var dir = btn.getAttribute("data-kg-dir") === "left" ? -640 : 640;
        target.scrollBy({ left: dir, behavior: reduced ? "auto" : "smooth" });
      });
    });
  }

  /* ------------------------------------------------------------ TILT CARDS */
  function initTilt() {
    if (reduced) return;
    document.querySelectorAll(".kg-tilt").forEach(function (el) {
      var base = { t: el.style.transform, s: el.style.boxShadow, b: el.style.borderColor };
      el.addEventListener("pointermove", function (ev) {
        var r = el.getBoundingClientRect();
        var px = (ev.clientX - r.left) / r.width - 0.5;
        var py = (ev.clientY - r.top) / r.height - 0.5;
        el.style.transition = "none";
        el.style.transform = "perspective(760px) rotateY(" + (px * 11) + "deg) rotateX(" + (-py * 11) + "deg) translateY(-5px)";
        el.style.boxShadow = "7px 11px 0 rgba(10,10,10,.92)";
        el.style.borderColor = "var(--color-black)";
        el.style.zIndex = "4";
      });
      el.addEventListener("pointerleave", function () {
        el.style.transition = "transform .4s cubic-bezier(.22,1,.36,1), box-shadow .4s";
        el.style.transform = base.t;
        el.style.boxShadow = base.s;
        el.style.borderColor = base.b;
        el.style.zIndex = "";
      });
    });
  }

  /* -------------------------------------------------------- 3-D GIFT CARD */
  function initGiftCard() {
    var gc = document.getElementById("kg-gcard");
    if (!gc || reduced) return;
    var restore = null;
    gc.addEventListener("pointermove", function (e) {
      var r = gc.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width - 0.5;
      var py = (e.clientY - r.top) / r.height - 0.5;
      gc.classList.remove("is-idle");
      gc.style.transition = "transform .1s linear";
      gc.style.transform = "perspective(1000px) rotateY(" + (px * 24) + "deg) rotateX(" + (-py * 18) + "deg) scale(1.04)";
    });
    gc.addEventListener("pointerleave", function () {
      gc.style.transition = "transform .6s cubic-bezier(.22,1,.36,1)";
      gc.style.transform = "";
      clearTimeout(restore);
      restore = setTimeout(function () {
        if (!gc.matches(":hover")) {
          gc.classList.add("is-idle");
          gc.style.transition = "";
          gc.style.transform = "";
        }
      }, 640);
    });
  }

  /* ------------------------------------------------------------ ADD TO CART
     The page now renders the shared `kitsters-card-product` card, whose
     button is `[data-add-to-cart]` (AJAX + cart drawer) rather than the old
     `.kg-pc__atc` form POST. Same handler shape as kitsters-home.js /
     kitsters-offers.js so behaviour is identical to the homepage.
     Delegated on document, so Gift-Finder result cards (cloned from the
     pool at match time) work without re-binding. */
  function initAddToCart() {
    var toast = document.querySelector("[data-toast]");
    var toastText = toast ? toast.querySelector("[data-toast-text]") : null;
    var toastClose = toast ? toast.querySelector("[data-toast-close]") : null;
    var badge = document.querySelector("[data-cart-count]");
    var toastTimer;

    function showToast(msg) {
      if (!toast || !toastText) return;
      toastText.textContent = msg;
      toast.classList.remove("is-open");
      void toast.offsetWidth;
      toast.classList.add("is-open");
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { toast.classList.remove("is-open"); }, 4000);
    }
    if (toastClose) {
      toastClose.addEventListener("click", function () {
        toast.classList.remove("is-open");
        clearTimeout(toastTimer);
      });
    }

    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-add-to-cart]");
      if (!btn || btn.disabled) return;
      if (!btn.closest(".kg")) return;
      e.preventDefault();

      var variantId = btn.getAttribute("data-variant-id");
      var name = btn.getAttribute("data-name") || "Item";
      if (!variantId) return;
      btn.disabled = true;

      // KitstersCart owns the drawer contract — which sections to request in the
      // add payload, and how to hand the response back so the drawer re-renders
      // and opens. On India this block queried Kalles' drawer element directly,
      // in eleven separate copies; here the assumption lives in one file
      // (assets/kitsters-cart-bridge.js) and this caller stays drawer-agnostic.
      var payload = { id: parseInt(variantId, 10), quantity: 1 };

      window.KitstersCart.addToCart(payload)
        .then(function () {
          showToast(name + " added to cart!");
          return fetch("/cart.js").then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          if (badge) {
            badge.textContent = cart.item_count;
            badge.classList.toggle("is-empty", cart.item_count === 0);
          }
        })
        .catch(function (err) {
          // The bridge attaches the /cart/add.js 422 body when there is one, so
          // a sold-out line says so instead of "please try again" (§10.28: an
          // error that cannot state its reason blames the customer for ours).
          showToast((err && err.cartError && err.cartError.description)
            || "Could not add to cart - please try again");
        })
        .finally(function () { btn.disabled = false; });
    });
  }

  ready(function () {
    initReveal();
    initHero();
    initStats();
    initCountdown();
    initFinder();
    initArrows();
    initTilt();
    initGiftCard();
    initAddToCart();
  });
})();
